import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, IconButton, Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import firebase from '../utilities/firebase';
import { StateManager } from '../utilities/stateManager';
import { recalculateInvoiceTax } from '../utilities/invoiceUtils';
import { v4 as uuidv4 } from 'uuid';
import { useStackZIndex, ModalStackProvider } from '../utilities/ModalStackContext';

export default function TradeModal({ open, onClose, stockNumber, trade, onSaved }) {
  const myZ = useStackZIndex();
  const isCreate = !trade;
  const [form, setForm] = React.useState({ year: '', make: '', model: '', vin: '', trade: '', payoff: '' });
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (trade) {
      setForm({
        year: String(trade.year || ''),
        make: trade.make || '',
        model: trade.model || '',
        vin: trade.vin || '',
        trade: trade.trade != null ? String(trade.trade) : '',
        payoff: trade.payoff != null ? String(trade.payoff) : '',
      });
    } else {
      setForm({ year: '', make: '', model: '', vin: '', trade: '', payoff: '' });
    }
  }, [trade, open]);

  const netTrade = (parseFloat(form.trade) || 0) - (parseFloat(form.payoff) || 0);

  const handleChange = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.year || !form.make || !form.model || !form.vin || !form.trade || form.payoff === '') {
      StateManager.setAlertAndOpen('Please fill in all required fields', 'error');
      return;
    }
    setSaving(true);
    try {
      const tradeAmt = parseFloat(form.trade) || 0;
      const payoffAmt = parseFloat(form.payoff) || 0;
      const data = {
        stock: stockNumber,
        year: parseInt(form.year, 10) || form.year,
        make: form.make,
        model: form.model,
        vin: (form.vin || '').trim().toUpperCase(),
        trade: tradeAmt,
        payoff: payoffAmt,
        netTrade: tradeAmt - payoffAmt,
      };

      let savedId;
      if (isCreate) {
        savedId = uuidv4();
        data.t = savedId;
        await firebase.firestore().collection('trades').doc(savedId).set(data);
        await firebase.firestore().doc(`deals/${stockNumber}`).set(
          { trades: firebase.firestore.FieldValue.arrayUnion(savedId) },
          { merge: true }
        );
      } else {
        savedId = trade.id;
        data.t = savedId;
        await firebase.firestore().collection('trades').doc(savedId).update(data);
      }

      await recalculateInvoiceTax(stockNumber);

      StateManager.setAlertAndOpen(isCreate ? 'Trade added' : 'Trade updated', 'success');
      if (onSaved) onSaved({ ...data, id: savedId });
      onClose();
    } catch (err) {
      console.error(err);
      StateManager.setAlertAndOpen(isCreate ? 'Failed to add trade' : 'Failed to update trade', 'error');
    }
    setSaving(false);
  };

  const USD = (v) =>
    v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

  return (
    <ModalStackProvider value={myZ}>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: myZ }}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{isCreate ? 'Add Trade-In' : 'Edit Trade-In'}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <TextField fullWidth size="small" label="Year" type="number" value={form.year} onChange={handleChange('year')} required />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" label="Make" value={form.make} onChange={handleChange('make')} required />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" label="Model" value={form.model} onChange={handleChange('model')} required />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="VIN" value={form.vin} onChange={handleChange('vin')} required inputProps={{ style: { textTransform: 'uppercase' } }} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="Trade Amount" type="number" value={form.trade} onChange={handleChange('trade')} required />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="Payoff Amount" type="number" value={form.payoff} onChange={handleChange('payoff')} required />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ px: 1.5, py: 1, bgcolor: 'grey.50', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Net Trade</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: netTrade >= 0 ? 'success.main' : 'error.main' }}>
                {USD(netTrade)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : (isCreate ? 'Add' : 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
    </ModalStackProvider>
  );
}
